import { useState } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { Clock, CheckCircle2, MapPin, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { taskStatusCircle } from "@/components/planner/DayTaskCard";
import { TaskDetailDrawer } from "@/components/planner/TaskDetailDrawer";
import { cn } from "@/lib/utils";

/** Una fila tal cual la devolvió `get_tasks` (ver CardGroup en agentLoop). */
export interface AssistantTaskCardItem {
  id?: string;
  title?: string;
  type?: string | null;
  status?: string | null;
  priority?: string | null;
  date?: string | null;
  who?: string | null;
  block?: string | null;
  greenhouse?: string | null;
  treatment?: string | null;
  pest?: string | null;
}

/** Lo que TaskDetailDrawer necesita para pintarse; se trae entero por id. */
type DrawerTask = Parameters<typeof TaskDetailDrawer>[0]["task"];

const TASK_SELECT =
  "*, greenhouses(name), blocks(name), treatments(name, mode), pests(name, translation_key)";

/**
 * Las tareas que cita el asistente, pintadas como en el Planificador en vez de
 * quedar en un párrafo.
 *
 * Dos reglas que sostienen esto:
 *
 *  - Lo que se pinta son las filas que DEVOLVIÓ la consulta, nunca lo que el
 *    modelo redacte. Una tarjeta se lee como un dato duro; si la escribiera el
 *    modelo, un error suyo pasaría por registro.
 *  - Al tocar una tarjeta se abre la ficha real de la tarea POR ENCIMA del chat.
 *    Al cerrarla sigues en la conversación: no se navega al Planificador, que
 *    perdería el hilo.
 */
export function AssistantTaskCards({ items }: { items: AssistantTaskCardItem[] }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [openTask, setOpenTask] = useState<DrawerTask | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const rows = items.filter((it) => it && (it.title || it.id));
  if (rows.length === 0) return null;

  const open = async (item: AssistantTaskCardItem) => {
    // Sin id no hay ficha que abrir: la tarjeta se queda como resumen y no se
    // finge un enlace que no lleva a ningún sitio.
    if (!item.id || loadingId) return;
    setLoadingId(item.id);
    try {
      const { data, error } = await supabase
        .from("planner_tasks")
        .select(TASK_SELECT)
        .eq("id", item.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        toast({ title: t("ipmAssistant.taskGone", "Esa tarea ya no existe"), variant: "destructive" });
        return;
      }
      setOpenTask(data as unknown as DrawerTask);
    } catch (err) {
      toast({
        title: t("common.error"),
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <>
      <div className="mt-2 space-y-1.5">
        {rows.map((item, i) => {
          const isDone = item.status === "completed";
          const where = [item.greenhouse, item.block].filter(Boolean).join(" · ");
          let day: string | null = null;
          if (item.date) {
            try { day = format(new Date(item.date), "d MMM"); } catch { day = null; }
          }
          const meta = [day, where].filter(Boolean).join(" · ");
          return (
            <button
              key={item.id ?? `${item.title}-${i}`}
              type="button"
              onClick={() => open(item)}
              disabled={!item.id}
              className={cn(
                "w-full text-left relative overflow-hidden rounded-[14px] border border-[#b6dded]/60 bg-white shadow-sm transition-all py-2.5 pl-3.5 pr-2.5",
                item.id ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 active:scale-[0.99]" : "cursor-default",
                loadingId === item.id && "opacity-60",
              )}
            >
              <div className="flex items-center gap-2.5">
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      "text-sm font-semibold leading-tight truncate",
                      isDone ? "line-through text-foreground/45" : "text-foreground",
                    )}
                  >
                    {item.title || item.treatment || "—"}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5 text-[11.5px] font-['Mulish'] font-semibold text-muted-foreground min-w-0">
                    {meta && (
                      <span className="flex items-center gap-1 min-w-0">
                        <Clock className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{meta}</span>
                      </span>
                    )}
                    {item.who && (
                      <span className="flex items-center gap-1 min-w-0">
                        <User className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">{item.who}</span>
                      </span>
                    )}
                    {!meta && !item.who && (
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />—
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className={cn(
                    "flex items-center justify-center w-[26px] h-[26px] rounded-full flex-shrink-0 border-[1.5px]",
                    taskStatusCircle(item.status ?? null),
                  )}
                >
                  {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* La ficha se monta sobre el chat: al cerrarla sigues en la conversación. */}
      <TaskDetailDrawer
        open={!!openTask}
        onOpenChange={(o) => { if (!o) setOpenTask(null); }}
        task={openTask}
      />
    </>
  );
}
